export type Vote = 'up' | 'down' | null

/**
 * One person's annotation of one concert.
 *
 * The shape matches the Firestore document exactly (`annotations/{uid}_{concertId}`)
 * so the cloud layer mirrors it rather than translating. `uid` is a stored field, not
 * just an id component: Firestore cannot filter on a document-id prefix, so the
 * "my annotations" query needs it on the document. Both `uid` and `sharedWith` stay
 * empty until sign-in.
 */
export interface Annotation {
  concertId: string
  uid: string | null
  vote: Vote
  note: string
  /**
   * The owner's display name, stamped at sign-in.
   *
   * Denormalised onto the annotation on purpose: a recipient reading a shared note
   * needs to know who wrote it, and carrying the name here means the shared view is
   * one query with no join and no second collection to read.
   */
  ownerName: string | null
  /**
   * Terningkast — a 1–6 rating, or null.
   *
   * Beside `vote`, not instead of it: a thumb marks what you plan to see, a die is a
   * verdict on what you saw. Records written before this field existed parse as
   * `undefined`, which is why every path that admits an annotation from outside
   * normalises it (see `createAnnotationStore`'s load loop and `mergeByUpdatedAt`).
   * Firestore rejects an undefined field value outright, so an unnormalised record
   * fails every upload rather than merely reading oddly.
   */
  dice: number | null
  /** ISO 8601. The merge key — last write wins. */
  updatedAt: string
  /** uids this annotation has been shared with. Only ever written server-side. */
  sharedWith: string[]
  /**
   * Who may read this annotation: the owner, plus their accepted friends (KTD1).
   *
   * Optional, and never read back off a stored record. The client composes it fresh on
   * every upload from the live friend list (`readersFor` in store/sync.ts) and hands it
   * to `upsert`; nothing local ever consumes it. That is deliberate on two counts: a
   * stale local copy can never re-grant a friend the server has already removed, and no
   * load path has to normalise it — so unlike `dice`, this field cannot leak
   * `undefined` into a write Firestore would reject outright.
   */
  readers?: string[]
}

/**
 * Someone else's annotation, shared with the current user.
 *
 * `ownerName` is restated rather than narrowed: a friend's annotation is kept even when
 * the document carries no display name. Google does not guarantee one, and dropping
 * those rows would both hide a real note and make the last level of the name fallback
 * (`friendPrefix`'s 'En venn') unreachable — dead code guarding a case that does occur.
 * Rendering handles the absence; the read path does not get to decide it away.
 */
export interface SharedAnnotation extends Annotation {
  ownerName: string | null
}

/**
 * Which read path someone else's annotation reached this device through.
 *
 * `shared` is an explicit grant the server wrote into `sharedWith`; `visible` is the
 * friend feed, where the owner listed this user in `readers` (KTD1, KTD2). The two are
 * kept as separate sets rather than folded together on arrival because each is a live
 * Firestore listener that replaces its own snapshot wholesale — one array for both
 * would let a `visible` snapshot silently wipe everything `shared` had delivered, and
 * the two queries have no way to know about each other's rows.
 *
 * The same document can legitimately arrive through both at once: a friend who also
 * shared a concert explicitly matches `sharedWith` and `readers` alike. Deduplication
 * is therefore the reader's job, not the writer's — see `sharedAnnotations`.
 */
export type SharedSource = 'shared' | 'visible'

/**
 * Where a friendship stands, from this user's side.
 *
 * `incoming` is a request waiting on the user, `outgoing` one they are waiting for.
 * Only `accepted` grants anything: until then neither party sees the other's
 * annotations (R29), which is why proposing never touches the `uids` array the
 * Firestore rules bound `readers` by (KTD4).
 */
export type FriendStatus = 'incoming' | 'outgoing' | 'accepted'

/**
 * One entry from `friendLists/{uid}`.friends — a friend, or a request either way.
 *
 * The address is here because the day view's name prefix needs one and the annotation
 * document has none: `ownerName` there is a Google display name, which is a different
 * thing from what the user typed when they added this person (KTD6, R14). Both fields
 * are nullable, because a friendship proposed to an address that has never signed in
 * exists before anything is known about who is on the other end (R9).
 */
export interface Friend {
  uid: string
  email: string | null
  name: string | null
  status: FriendStatus
}

/**
 * Whether writes survive a reload.
 *
 * `ephemeral` means the store is running in memory — iOS private mode can throw on
 * localStorage writes. The UI surfaces this rather than showing a "saved" state that
 * is not true.
 */
export type StorageMode = 'persistent' | 'ephemeral'

export interface AnnotationStore {
  getAnnotation(concertId: string): Annotation | null
  /**
   * Other people's annotations of this concert that the current user may read.
   *
   * The union across every `SharedSource`, deduped: one person's annotation of one
   * concert appears once however many read paths delivered it.
   */
  sharedAnnotations(concertId: string): SharedAnnotation[]
  allAnnotations(): Annotation[]
  setVote(concertId: string, vote: Vote): void
  setNote(concertId: string, note: string): void
  /** Terningkast, 1–6, or null to clear it. */
  setDice(concertId: string, dice: number | null): void
  /** Returns an unsubscribe function. */
  subscribe(listener: () => void): () => void
  readonly storageMode: StorageMode
}

export function emptyAnnotation(concertId: string): Annotation {
  return {
    concertId,
    uid: null,
    ownerName: null,
    vote: null,
    note: '',
    dice: null,
    updatedAt: '',
    sharedWith: [],
  }
}

/** Normalises a record that predates a field, so `undefined` never escapes the store. */
export function withDefaults(annotation: Annotation): Annotation {
  return { ...annotation, dice: annotation.dice ?? null }
}

/** True when an annotation carries nothing worth persisting. */
export function isBlank(annotation: Annotation): boolean {
  return (
    annotation.vote === null && annotation.note.trim() === '' && (annotation.dice ?? null) === null
  )
}
